<template>
  <div
    class="TagReceipt"
    :class="[`TagReceipt--${normalizedStatus}`, `TagReceipt--${size}`]"
  >
    <Tag
      class="TagReceipt-badge"
      :type="badgeType"
      variant="filled"
      :size="badgeSize"
    >
      <slot />
    </Tag>

    <span
      v-if="totalText"
      class="TagReceipt-total"
    >{{ totalText }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Tag from '@cvo/ui/components/atoms/Tag/Tag.vue';
import type { TagProps } from '@cvo/ui/components/atoms/Tag/Tag.model';
import { INVOICE_STATUS_MAP } from '@cvo/ui/composables/useInvoiceStatus';
import type { TagReceiptProps } from './TagReceipt.model';

const props = withDefaults(defineProps<TagReceiptProps>(), {
  size: 'lg',
});

const BADGE_SIZE_MAP: Record<Required<TagReceiptProps>['size'], TagProps['size']> = {
  sm: 'xs',
  lg: 'sm',
};

const normalizedStatus = computed(() => props.status.toLowerCase().replaceAll('_', '-'));
const badgeType = computed(() => INVOICE_STATUS_MAP[props.status].tagStatus);
const badgeSize = computed(() => BADGE_SIZE_MAP[props.size]);
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .TagReceipt {
    display: inline-flex;
    align-items: center;
    border-style: solid;
    border-width: vars.$atoms-tag-border-stroke;
    border-radius: vars.$atoms-tag-border-radius;
    background-color: var(--tag-receipt-bg-color);
    border-color: var(--tag-receipt-border-color);

    .TagReceipt-badge.Tag {
      border-color: transparent;
    }

    &-total {
      white-space: nowrap;
      color: var(--tag-receipt-text-color);
    }

    &--lg {
      gap: vars.$dimensions-d-2;

      .TagReceipt-total {
        // TODO CVO-19983: Replace with semantic token once available
        @include text.text(sm, vars.$font-weight-text-700);
        padding-inline-end: vars.$dimensions-d-2;
      }
    }

    &--sm {
      gap: vars.$dimensions-d-1;

      .TagReceipt-total {
        // TODO CVO-19983: Replace with semantic token once available
        @include text.text(xs, vars.$font-weight-text-700);
        padding-inline-end: vars.$dimensions-d-1;
      }
    }

    &--paid {
      --tag-receipt-bg-color: #{vars.$molecules-tag-receipt-paid-total-bg-color};
      --tag-receipt-border-color: #{vars.$molecules-tag-receipt-paid-total-border-color};
      --tag-receipt-text-color: #{vars.$molecules-tag-receipt-paid-total-text-color};
    }

    &--unpaid {
      --tag-receipt-bg-color: #{vars.$molecules-tag-receipt-not-paid-total-bg-color};
      --tag-receipt-border-color: #{vars.$molecules-tag-receipt-not-paid-total-border-color};
      --tag-receipt-text-color: #{vars.$molecules-tag-receipt-not-paid-total-text-color};
    }

    &--refunded {
      --tag-receipt-bg-color: #{vars.$molecules-tag-receipt-refunded-total-bg-color};
      --tag-receipt-border-color: #{vars.$molecules-tag-receipt-refunded-total-border-color};
      --tag-receipt-text-color: #{vars.$molecules-tag-receipt-refunded-total-text-color};
    }

    &--partially-paid {
      --tag-receipt-bg-color: #{vars.$molecules-tag-receipt-partially-paid-total-bg-color};
      --tag-receipt-border-color: #{vars.$molecules-tag-receipt-partially-paid-total-border-color};
      --tag-receipt-text-color: #{vars.$molecules-tag-receipt-partially-paid-total-text-color};
    }

    &--partially-refunded {
      --tag-receipt-bg-color: #{vars.$molecules-tag-receipt-partially-refunded-total-bg-color};
      --tag-receipt-border-color: #{vars.$molecules-tag-receipt-partially-refunded-total-border-color};
      --tag-receipt-text-color: #{vars.$molecules-tag-receipt-partially-refunded-total-text-color};
    }
  }
}
</style>
